import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ComissaoMotoristaService from "../../services/resource/ComissaoMotoristaService";
import { useAuth } from "../../context/authContext";
import LogService from "../../services/resource/logService";
import { returnDate } from "../../utils/formatDate";
import FilialService from "../../services/resource/filialService";
import TabelaVolumeEntregue from "./TabelaVolumeEntregue";
import SapServiceRetornoEntrega from "../../services/resource/SapServiceRetornoEntrega";
import SapServiceEntregaVolume from "../../services/resource/SapServiceEntregaVolume";
import TabelaEntregaVolume from "./TabelaEntregaVolume";
import TabelaRetornosSap from "./TabelaRetorno";
import DashboardEntregaRetorno from "./DashboardEntregaRetorno";
import ComissaoService from "../../services/resource/ComissaoService";

const formatPercent = (value) =>
    value != null ? `${Number(value).toFixed(2)}%` : "—";

const formatCubo = (value) =>
    value != null ? Number(value).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—";

// ─── Tabs ─────────────────────────────────────────────────────────────────────
const TABS = [
    { id: "lista", label: "Acompanhamento", icon: "fa fa-list" },
    { id: "dashboard", label: "Dashboard", icon: "fa fa-bar-chart" },
    { id: "ranking", label: "Ranking", icon: "fa fa-trophy" },
    { id: "entregas", label: "Base de Entregas", icon: "fa fa-truck" },
    { id: "retornos", label: "Base de Retornos", icon: "fa fa-reply" },
];

const MESES = [
    { label: "Janeiro", value: 1 }, { label: "Fevereiro", value: 2 },
    { label: "Março", value: 3 }, { label: "Abril", value: 4 },
    { label: "Maio", value: 5 }, { label: "Junho", value: 6 },
    { label: "Julho", value: 7 }, { label: "Agosto", value: 8 },
    { label: "Setembro", value: 9 }, { label: "Outubro", value: 10 },
    { label: "Novembro", value: 11 }, { label: "Dezembro", value: 12 },
];

const ANOS = [{ label: "2026", value: 2026 }, { label: "2027", value: 2027 }];

// ─── StatCard ─────────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, sub, color = "teal" }) {
    const colors = {
        teal: "bg-teal-50   border-teal-200   text-teal-700",
        blue: "bg-blue-50   border-blue-200   text-blue-700",
        amber: "bg-amber-50  border-amber-200  text-amber-700",
        rose: "bg-rose-50   border-rose-200   text-rose-700",
        violet: "bg-violet-50 border-violet-200 text-violet-700",
        gray: "bg-muted border-border text-foreground",
        orange: "bg-orange-50 border-orange-200 text-orange-700",
    };
    return (
        <div className={`rounded-xl border p-4 flex items-start gap-3 ${colors[color]}`}>
            <div className="text-xl mt-0.5 w-5 flex-shrink-0 text-center">
                <i className={icon} />
            </div>
            <div>
                <p className="text-xs font-medium opacity-70">{label}</p>
                <p className="text-lg font-bold leading-tight">{value}</p>
                {sub && <p className="text-xs opacity-60 mt-0.5">{sub}</p>}
            </div>
        </div>
    );
}

// ─── MiniBar ─────────────────────────────────────────────────────────────────
function MiniBar({ label, value, max, color = "#14b8a6", suffix = "" }) {
    const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
    return (
        <div className="flex items-center gap-2 text-xs">
            <span className="w-32 truncate text-muted-foreground flex-shrink-0">{label}</span>
            <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                <div
                    className="h-2 rounded-full transition-all duration-500"
                    style={{ width: `${pct}%`, background: color }}
                />
            </div>
            <span className="w-24 text-right font-semibold text-foreground">
                {typeof value === "number"
                    ? value.toLocaleString("pt-BR", { maximumFractionDigits: 2 })
                    : value}
                {suffix}
            </span>
        </div>
    );
}

// ─── Dashboard Tab ────────────────────────────────────────────────────────────
function DashboardTab({ comissoes }) {
    const stats = useMemo(() => {
        if (!comissoes.length) return null;

        const totalEntregues = comissoes.reduce((a, c) => a + (c.totalCubosEntregues ?? 0), 0);
        const totalRetornados = comissoes.reduce((a, c) => a + (c.totalCubosRetornados ?? 0), 0);
        const total1Aux = comissoes.reduce((a, c) => a + (c.cubosEntregue1Auxiliar ?? 0), 0);
        const total2Aux = comissoes.reduce((a, c) => a + (c.cubosEntregue2Auxiliar ?? 0), 0);
        const mediaEntregues = totalEntregues / comissoes.length;
        const percentualRetorno = totalEntregues > 0 ? (totalRetornados / totalEntregues) * 100 : 0;

        const motoristas = comissoes.filter(c => c.funcionarioCargo?.toLowerCase().includes("motorista")).length;
        const auxiliares = comissoes.filter(c => c.funcionarioCargo?.toLowerCase().includes("auxiliar")).length;

        // por filial — soma cubos entregues
        const porFilial = comissoes.reduce((acc, c) => {
            const f = c.filialNome ?? "Sem filial";
            if (!acc[f]) acc[f] = { entregues: 0, retornados: 0, count: 0 };
            acc[f].entregues += c.totalCubosEntregues ?? 0;
            acc[f].retornados += c.totalCubosRetornados ?? 0;
            acc[f].count++;
            return acc;
        }, {});

        // por cargo
        const porCargo = comissoes.reduce((acc, c) => {
            const cargo = c.funcionarioCargo ?? "Outros";
            if (!acc[cargo]) acc[cargo] = { entregues: 0, count: 0 };
            acc[cargo].entregues += c.totalCubosEntregues ?? 0;
            acc[cargo].count++;
            return acc;
        }, {});

        const maxFilial = Math.max(...Object.values(porFilial).map(v => v.entregues));
        const maxCargo = Math.max(...Object.values(porCargo).map(v => v.entregues));

        const top5 = [...comissoes]
            .sort((a, b) => (b.totalCubosEntregues ?? 0) - (a.totalCubosEntregues ?? 0))
            .slice(0, 5);

        const mediaConclusao = comissoes
            .filter(c => c.percentualConclusao != null)
            .reduce((a, c, _, arr) => a + (c.percentualConclusao ?? 0) / arr.length, 0);

        return {
            totalEntregues, totalRetornados, total1Aux, total2Aux,
            mediaEntregues, percentualRetorno,
            motoristas, auxiliares,
            porFilial, porCargo, maxFilial, maxCargo,
            top5, mediaConclusao,
            totalFuncionarios: comissoes.length,
        };
    }, [comissoes]);

    if (!comissoes.length) return (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <i className="fa fa-bar-chart text-5xl mb-4 opacity-30" />
            <p className="text-sm">Nenhum dado disponível. Selecione um período.</p>
        </div>
    );

    return (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="p-4 space-y-5">

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                <StatCard icon="fa fa-users" label="Funcionários" value={stats.totalFuncionarios} color="blue" />
                <StatCard icon="fa fa-truck" label="Motoristas" value={stats.motoristas} color="teal" />
                <StatCard icon="fa fa-user" label="Auxiliares" value={stats.auxiliares} color="violet" />
                <StatCard icon="fa fa-cube" label="Total Entregue" value={`${formatCubo(stats.totalEntregues)} `} color="teal" />
                <StatCard icon="fa fa-reply" label="Total Retornado" value={`${formatCubo(stats.totalRetornados)} `} color="orange" />
                <StatCard icon="fa fa-percent" label="% Retorno" value={`${stats.percentualRetorno.toFixed(2)}%`} color="rose" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">

                {/* Por Filial */}
                <div className="rounded-xl border border-border bg-card p-4 shadow-card">
                    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-foreground">
                        <i className="fa fa-building text-teal-600" /> Cubos Entregues por Filial
                    </h3>
                    <div className="space-y-2.5">
                        {Object.entries(stats.porFilial)
                            .sort((a, b) => b[1].entregues - a[1].entregues)
                            .map(([nome, { entregues }]) => (
                                <MiniBar key={nome} label={nome} value={entregues} max={stats.maxFilial} suffix=" " />
                            ))}
                    </div>
                </div>

                {/* Por Cargo */}
                <div className="rounded-xl border border-border bg-card p-4 shadow-card">
                    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-foreground">
                        <i className="fa fa-id-badge text-blue-600" /> Cubos Entregues por Cargo
                    </h3>
                    <div className="space-y-2.5">
                        {Object.entries(stats.porCargo)
                            .sort((a, b) => b[1].entregues - a[1].entregues)
                            .map(([cargo, { entregues }]) => (
                                <MiniBar key={cargo} label={cargo} value={entregues} max={stats.maxCargo} color="#3b82f6" suffix=" " />
                            ))}
                    </div>
                </div>

                {/* Top 5 */}
                <div className="rounded-xl border border-border bg-card p-4 shadow-card">
                    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-foreground">
                        <i className="fa fa-trophy text-amber-500" /> Top 5 Maiores Volumes
                    </h3>
                    <div className="space-y-2">
                        {stats.top5.map((c, i) => (
                            <div key={c.funcionarioId} className="flex items-center gap-2 text-xs">
                                <span className={`w-5 h-5 rounded-full flex items-center justify-center font-bold text-white text-[10px] flex-shrink-0
                                    ${i === 0 ? "bg-amber-400" : i === 1 ? "bg-gray-400" : i === 2 ? "bg-orange-400" : "bg-muted text-muted-foreground"}`}>
                                    {i + 1}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <p className="truncate font-medium text-foreground">{c.funcionarioNome}</p>
                                    <p className="text-muted-foreground truncate">{c.filialNome}</p>
                                </div>
                                <span className="font-bold text-teal-700 flex-shrink-0">
                                    {formatCubo(c.totalCubosEntregues)}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* % Conclusão média */}
                <div className="rounded-xl border border-border bg-card p-4 shadow-card">
                    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-foreground">
                        <i className="fa fa-check-circle text-teal-600" /> Média de Conclusão de Rota
                    </h3>
                    <div className="flex flex-col items-center justify-center py-4">
                        <div className="relative w-28 h-28">
                            <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                                <circle cx="50" cy="50" r="40" fill="none" stroke="#e5e7eb" strokeWidth="10" />
                                <circle
                                    cx="50" cy="50" r="40" fill="none"
                                    stroke="#14b8a6" strokeWidth="10"
                                    strokeDasharray={`${2 * Math.PI * 40 * stats.mediaConclusao / 100} ${2 * Math.PI * 40}`}
                                    strokeLinecap="round"
                                />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-xl font-bold text-teal-700">{stats.mediaConclusao.toFixed(1)}%</span>
                            </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">Média geral de conclusão</p>
                    </div>
                </div>

                {/* Distribuição 1 Aux / 2 Aux / Retorno */}
                <div className="rounded-xl border border-border bg-card p-4 shadow-card">
                    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-foreground">
                        <i className="fa fa-pie-chart text-violet-600" /> Distribuição de Volumes
                    </h3>
                    <div className="space-y-3 mt-2">
                        {[
                            { label: "Entregue c/ 1 Auxiliar", value: stats.total1Aux, max: stats.totalEntregues, color: "#8b5cf6" },
                            { label: "Entregue c/ 2 Auxiliares", value: stats.total2Aux, max: stats.totalEntregues, color: "#14b8a6" },
                            { label: "Retornado", value: stats.totalRetornados, max: stats.totalEntregues, color: "#f97316" },
                        ].map(item => (
                            <div key={item.label}>
                                <div className="flex justify-between text-xs mb-1">
                                    <span className="text-muted-foreground">{item.label}</span>
                                    <span className="font-semibold">{formatCubo(item.value)} </span>
                                </div>
                                <div className="bg-muted rounded-full h-2 overflow-hidden">
                                    <div
                                        className="h-2 rounded-full"
                                        style={{
                                            width: `${item.max > 0 ? Math.min((item.value / item.max) * 100, 100) : 0}%`,
                                            background: item.color,
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Resumo geral */}
                <div className="rounded-xl border border-border bg-card p-4 shadow-card">
                    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-foreground">
                        <i className="fa fa-info-circle text-blue-600" /> Resumo Geral
                    </h3>
                    <div className="space-y-2 text-xs">
                        {[
                            { label: "Total entregue", value: `${formatCubo(stats.totalEntregues)} `, bold: true },
                            { label: "Entregue c/ 1 auxiliar", value: `${formatCubo(stats.total1Aux)} ` },
                            { label: "Entregue c/ 2 auxiliares", value: `${formatCubo(stats.total2Aux)} ` },
                            { label: "Total retornado", value: `${formatCubo(stats.totalRetornados)} `, bold: true },
                            { label: "% Retorno", value: `${stats.percentualRetorno.toFixed(2)}%` },
                            { label: "Média por funcionário", value: `${formatCubo(stats.mediaEntregues)} ` },
                            { label: "Motoristas", value: stats.motoristas },
                            { label: "Auxiliares", value: stats.auxiliares },
                        ].map(item => (
                            <div key={item.label} className="flex justify-between">
                                <span className="text-muted-foreground">{item.label}</span>
                                <span className={item.bold ? "font-bold text-teal-700" : "font-medium text-foreground"}>{item.value}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </motion.div>
    );
}

// ─── Ranking Tab ──────────────────────────────────────────────────────────────
function RankingTab({ comissoes }) {
    const [sortKey, setSortKey] = useState("totalCubosEntregues");
    const [filterCargo, setFilterCargo] = useState("todos");

    const cargos = useMemo(() => {
        const set = new Set(comissoes.map(c => c.funcionarioCargo ?? "Outros"));
        return ["todos", ...Array.from(set)];
    }, [comissoes]);

    const ranked = useMemo(() => {
        const list = filterCargo === "todos"
            ? comissoes
            : comissoes.filter(c => c.funcionarioCargo === filterCargo);

        const getVal = (c) => {
            if (sortKey === "totalCubosEntregues") return c.totalCubosEntregues ?? 0;
            if (sortKey === "totalCubosRetornados") return c.totalCubosRetornados ?? 0;
            if (sortKey === "percentualRetorno") return c.percentualRetornoTotal ?? 0;
            if (sortKey === "percentualConclusao") return c.percentualConclusao ?? 0;
            if (sortKey === "totalRotas") return c.quantidadesEscalasDeRota ?? 0;
            return 0;
        };

        return [...list].sort((a, b) => getVal(b) - getVal(a));
    }, [comissoes, sortKey, filterCargo]);

    const medalColors = ["bg-amber-400", "bg-gray-300", "bg-orange-400"];

    const getDisplayValue = (c) => {
        if (sortKey === "totalCubosEntregues") return `${formatCubo(c.totalCubosEntregues)} `;
        if (sortKey === "totalCubosRetornados") return `${formatCubo(c.totalCubosRetornados)} `;
        if (sortKey === "percentualRetorno") return formatPercent(c.percentualRetornoTotal);
        if (sortKey === "percentualConclusao") return formatPercent(c.percentualConclusao);
        if (sortKey === "totalRotas") return c.quantidadesEscalasDeRota ?? "—";
        return "—";
    };

    if (!comissoes.length) return (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <i className="fa fa-trophy text-5xl mb-4 opacity-30" />
            <p className="text-sm">Nenhum dado disponível.</p>
        </div>
    );

    return (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="p-4">
            <div className="flex flex-wrap gap-3 mb-4 items-center">
                <div className="flex gap-2 flex-wrap">
                    {[
                        { key: "totalCubosEntregues", label: "Cubos Entregues" },
                        { key: "totalCubosRetornados", label: "Cubos Retornados" },
                        { key: "percentualRetorno", label: "% Retorno" },
                        { key: "percentualConclusao", label: "% Conclusão" },
                        { key: "totalRotas", label: "Total Rotas" },
                    ].map(opt => (
                        <button
                            key={opt.key}
                            onClick={() => setSortKey(opt.key)}
                            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors duration-150
                                ${sortKey === opt.key
                                    ? "bg-teal-600 text-white border-teal-600"
                                    : "bg-card text-muted-foreground border-border hover:border-teal-300"}`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
                <select
                    value={filterCargo}
                    onChange={e => setFilterCargo(e.target.value)}
                    className="ml-auto text-xs border border-input rounded-lg px-3 py-1.5 bg-card text-foreground"
                >
                    {cargos.map(c => (
                        <option key={c} value={c}>{c === "todos" ? "Todos os Cargos" : c}</option>
                    ))}
                </select>
            </div>

            <div className="space-y-2">
                {ranked.map((c, i) => (
                    <div
                        key={c.funcionarioId}
                        className="flex items-center gap-3 bg-card border border-border rounded-xl p-3 shadow-card hover:shadow-soft transition-shadow duration-150"
                    >
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-white text-xs flex-shrink-0
                            ${i < 3 ? medalColors[i] : "bg-muted text-muted-foreground"}`}>
                            {i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm text-foreground truncate">{c.funcionarioNome}</p>
                            <p className="text-xs text-muted-foreground">{c.filialNome} · {c.funcionarioCargo} · {c.funcionarioCodigo}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                            <p className="font-bold text-teal-700 text-sm">{getDisplayValue(c)}</p>
                        </div>
                    </div>
                ))}
            </div>
        </motion.div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function VolumeEntregue() {
    const { user } = useAuth();
    const isRoleComissao = user.perfis.includes("COMISSOES");

    const now = new Date();
    let anoInicial = now.getFullYear();
    let mesInicial = now.getMonth() + 1;
    if (now.getDate() >= 27) {
        if (mesInicial === 12) { mesInicial = 1; anoInicial += 1; }
        else { mesInicial += 1; }
    }

    const comissaoService = useMemo(() => new ComissaoService(), []);
    const entregaService = useMemo(() => new SapServiceEntregaVolume(), []);
    const retornoService = useMemo(() => new SapServiceRetornoEntrega(), []);
    const filialService = useMemo(() => new FilialService(), []);
    const logService = useMemo(() => new LogService(), []);

    const [activeTab, setActiveTab] = useState("lista");
    const [loading, setLoading] = useState(true);
    const [loadingRetorno, setLoadingRetorno] = useState(true);
    const [loadingEntrega, setLoadingEntrega] = useState(true);
    const [ano, setAno] = useState(anoInicial);
    const [mes, setMes] = useState(mesInicial);
    const [log, setLog] = useState({ data: null });
    const [comissoes, setComissoes] = useState([]);
    const [filiais, setFiliais] = useState([]);
    const [filialId, setFilialId] = useState(null);
    const [entregas, setEntregas] = useState([]);
    const [retornos, setRetornos] = useState([]);

    useEffect(() => {
        buscarLogs();
    }, []);

    useEffect(() => {
        buscarComissoes();
        buscarFiliais();
    }, [ano, mes, filialId]);

    useEffect(() => {
        if (activeTab === "entregas") {
            listaEntregas();
        }

        if (activeTab === "retornos") {
            listaRetornos();
        }
    }, [ano, mes, filialId, activeTab]);


    const listaEntregas = () => {
        setLoadingEntrega(true);
        entregaService.lista({ ano, mes, comissoes: true, filialId: isRoleComissao ? user.filialId : filialId }).then(res => {
            setEntregas(res.data);
        }).finally(() => setLoadingEntrega(false))
    }

    const listaRetornos = () => {
        setLoadingRetorno(true)
        retornoService.lista({ ano, mes, comissoes: true, filialId: isRoleComissao ? user.filialId : filialId }).then(res => {
            setRetornos(res.data)
        }).finally(() => setLoadingRetorno(false))
    }

    const buscarLogs = () => {
        logService.buscarLogPorModulo("GERACAO_COMISSAO")
            .then(res => setLog(res.data))
            .catch(e => console.error(e));
    };

    const buscarFiliais = () => {
        filialService.listarComComissao(ano, mes)
            .then(res => setFiliais(res.data));
    };

    const buscarComissoes = () => {
        setLoading(true);
        comissaoService.listar({
            ano,
            mes,
            filialId: isRoleComissao ? user.filialId : filialId,
        })
            .then(res => setComissoes(res.data))
            .finally(() => setLoading(false));
    };

    return (
        <div className="bg-background">

            {/* ── Header ── */}
            <div className="bg-card border-b border-border px-4 pt-4">
                <div className="flex flex-wrap justify-between gap-3 mb-3">
                    <div>
                        <h1 className="font-display text-xl font-bold text-foreground">Acompanhamento de Volume Entregue</h1>
                        <p className="text-sm text-muted-foreground">
                            Cubos entregues e retornados por funcionário
                            {log.data && (
                                <span className="text-xs fade-in"> | Atualizado pela última vez {returnDate(log.data)}</span>
                            )}
                        </p>
                    </div>

                    <div className="flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 shadow-card">
                        {!isRoleComissao && (
                            <select
                                value={filialId ?? ""}
                                onChange={e => setFilialId(e.target.value)}
                                className="w-32 border-0 bg-transparent text-sm font-medium text-foreground focus:outline-none cursor-pointer"
                            >
                                <option value="">Todas Filiais</option>
                                {filiais.map(f => (
                                    <option key={f.id} value={f.id}>{f.nome}</option>
                                ))}
                            </select>
                        )}
                        <i className="fa fa-calendar-alt text-muted-foreground text-sm mr-1" />
                        <select
                            value={mes}
                            onChange={e => setMes(e.target.value)}
                            className="w-32 border-0 bg-transparent text-sm font-medium text-foreground focus:outline-none cursor-pointer"
                        >
                            {MESES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                        </select>
                        <span className="text-border">|</span>
                        <select
                            value={ano}
                            onChange={e => setAno(Number(e.target.value))}
                            className="border-0 bg-transparent text-sm font-medium text-foreground focus:outline-none cursor-pointer"
                        >
                            {ANOS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                        </select>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex flex-wrap gap-0 -mb-px">
                    {TABS.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors duration-150 whitespace-nowrap
                                ${activeTab === tab.id
                                    ? "border-primary text-primary bg-primary/5"
                                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"}`}
                        >
                            <i className={tab.icon} />
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── Tab Content ── */}
            <AnimatePresence mode="wait">
                {activeTab === "lista" && (
                    <motion.div key="lista" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <TabelaVolumeEntregue data={comissoes} loading={loading} />
                    </motion.div>
                )}
                {activeTab === "dashboard" && (
                    <motion.div key="dashboard" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <DashboardTab comissoes={comissoes} />
                    </motion.div>
                )}
                {activeTab === "ranking" && (
                    <motion.div key="ranking" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <RankingTab comissoes={comissoes} />
                    </motion.div>
                )}
                {activeTab === "entregas" && (
                    <motion.div key="entregas" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <TabelaEntregaVolume data={entregas} loading={loadingEntrega} />
                    </motion.div>
                )}
                {activeTab === "retornos" && (
                    <motion.div key="retornos" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <TabelaRetornosSap data={retornos} loading={loadingRetorno} />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
